import React from "react";

import { useTranslation } from "utils";

import { PRACTICE_SLUG, PracticeFCPO, PracticeSlug, ZERO_UUID } from "models";

import { Button, Flex, Icon } from "components";

import { PracticeChip } from "pages/app/historical-practice-data-table-view/components/PracticeChip";
import { PracticeTableTooltip } from "pages/app/historical-practice-data-table-view/components/PracticeTableTooltip";

type PracticeColumnProps<T> = {
  practices: T[] | null;
  practicesData: Array<{ id: string; name: string }> | undefined;
  getPracticeId: (item: T) => string;
  getItemKey: (item: T) => string;
  isExpanded: boolean;
  practiceType: PracticeSlug;
  isTillageDepthRequired?: boolean;
  onClick: () => void;
  onAddClick?: () => void;
  isEditable: boolean;
  disabled?: boolean;
  // suppresses the missing tillage depth / end date warnings, e.g. for a crop that doesn't affect the payout
  hideMissingData?: boolean;
};

// shared by historical-practice-data-table-view/MissingDataTable.tsx and all-fields-data-table-view/AllFieldsDataTable.tsx
export const PracticeColumn = <T,>({
  practices,
  practicesData,
  getPracticeId,
  getItemKey,
  isExpanded,
  practiceType,
  isTillageDepthRequired,
  onClick,
  onAddClick,
  isEditable,
  disabled,
  hideMissingData = false,
}: PracticeColumnProps<T>) => {
  const { t } = useTranslation(["historicalPracticeDataTableView"]);

  if (practices === null) return null;

  const hasPractices = practices.length > 0;

  const getPracticeName = (item: T): string | undefined => {
    const practiceId = getPracticeId(item);
    return practicesData?.find((practice) => practice.id === practiceId)?.name;
  };

  const tillagesWithMissingDepth =
    !hideMissingData && practiceType === PRACTICE_SLUG.TILLAGES && isTillageDepthRequired
      ? practices
          .filter((practice) => (practice as PracticeFCPO).assignedPracticeId !== ZERO_UUID && (practice as PracticeFCPO).tillageDepth === null)
          .map((practice) => getPracticeId(practice))
      : [];

  const practicesWithMissingEndDate =
    !hideMissingData && (practiceType === PRACTICE_SLUG.TILLAGES || practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS)
      ? practices
          .filter((practice) => (practice as PracticeFCPO).assignedPracticeId !== ZERO_UUID && (practice as PracticeFCPO).endDate === null)
          .map((practice) => getPracticeId(practice))
      : [];

  const itemsToShowAsChips = isExpanded ? practices : practices.slice(0, 1);

  return (
    <Flex justify="flex-start" align="flex-start" vertical={isExpanded} gap="0.5rem">
      {hasPractices ? (
        <>
          {itemsToShowAsChips.map((item) => {
            const practiceName = getPracticeName(item);
            const practiceId = getPracticeId(item);
            const hasMissingData = tillagesWithMissingDepth.includes(practiceId) || practicesWithMissingEndDate.includes(practiceId);

            return (
              <PracticeChip
                key={getItemKey(item)}
                practiceName={practiceName}
                hasMissingData={hasMissingData}
                isEditable={isEditable && !disabled}
                onClick={onClick}
              />
            );
          })}
          {!isExpanded && practices.length > 1 && (
            <PracticeTableTooltip
              alwaysShow
              title={
                <Flex vertical gap="0.5rem">
                  {practices.slice(1).map((item) => {
                    const practiceName = getPracticeName(item);
                    return <div key={getItemKey(item)}>{practiceName}</div>;
                  })}
                </Flex>
              }
            >
              {({ ref }) => {
                const hasMissingData = practices.slice(1).some((item) => {
                  const practiceId = getPracticeId(item);
                  return tillagesWithMissingDepth.includes(practiceId) || practicesWithMissingEndDate.includes(practiceId);
                });
                return (
                  <div
                    data-no-row-select
                    role="button"
                    className={`historical-practice-data-table-view-practice-chip count-indicator ${hasMissingData ? "incomplete-practice" : ""}`}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onClick();
                    }}
                  >
                    {hasMissingData && <Icon size="xs" iconName="tabler/alert-circle" className="historical-practice-data-table-view-practice-alert-icon" />}
                    <span ref={ref}>{` + ${practices.length - 1}`}</span>
                  </div>
                );
              }}
            </PracticeTableTooltip>
          )}
        </>
      ) : (
        <Button
          type="text"
          className="historical-practice-data-table-view-add-practice-button"
          disabled={disabled}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            (onAddClick ?? onClick)();
          }}
        >
          {t("table.data.actions.add")}
        </Button>
      )}
    </Flex>
  );
};
